import "./statustext.scss";
import * as React from "react";
import { type HTMLProps, type RefObject } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { debounce } from "throttle-debounce";
import { type Override } from "../../../types/internal/utility";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";

type Props = typeof StatusText.defaultProps & Override<Omit<HTMLProps<HTMLDivElement>, "ref">, {
    value: string;
    errorText?: string;
    onChange: (value: string) => void;
    checkStatus: (value: string, callback: (results: boolean) => void) => void;
    checkStart?: () => void;
    renderInput?: (ref: RefObject<HTMLInputElement | null>, onChange: (value: string) => void) => React.ReactNode;
}>;

type State = {
    status: null | boolean;
};

export default class StatusText extends React.Component<Props, State> {
    public static defaultProps: {
        errorIcon: IconProp;
        errorClass: string;
    } = {
        errorIcon : "times",
        errorClass: "failed",
    };

    public input: RefObject<HTMLInputElement | null>;

    constructor(props: Props) {
        super(props);

        this.state = {
            status: null,
        };

        this.handleStatusCheck = debounce(500, this.handleStatusCheck.bind(this), {
            atBegin: false,
        });
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleChange = this.handleChange.bind(this);

        this.input = React.createRef();
    }

    public componentDidMount(): void {
        this.props.checkStatus(this.props.value, (response) => {
            this.setState({
                status: response,
            });
        });
    }

    protected handleStatusCheck(value: string): void {
        this.props.checkStatus(value, (result) => {
            this.setState({
                status: result,
            });
        });
    }

    protected handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.handleChange(e.currentTarget.value);
    }

    protected handleChange(value: string): void {
        this.setState({
            status: null,
        });

        this.props.onChange(value);

        if(typeof this.props.checkStart === "function") {
            this.props.checkStart();
        }

        this.handleStatusCheck(value);
    }

    public focus(): void {
        this.input?.current?.focus();
    }

    public render(): React.ReactNode {
        const {
            errorText,
            onChange,
            checkStatus,
            checkStart,
            renderInput,
            errorIcon,
            errorClass,
            ...other
        } = this.props;

        let status = null;
        const statusProps: HTMLProps<HTMLDivElement> & {
            "data-tooltip"?: string;
        } = {
            className: "status",
        };

        if(this.props.value.length) {
            if(this.state.status === null) {
                status = (
                    <FontAwesomeLight icon="spinner" className="fa-pulse" />
                );
            } else if(this.state.status) {
                statusProps.className += " passed";
                status = (
                    <FontAwesomeLight icon="check" />
                );
            } else {
                if(this.props.errorText) {
                    statusProps["data-tooltip"] = errorText;
                }

                statusProps.className += ` ${this.props.errorClass}`;
                status = (
                    <FontAwesomeLight icon={this.props.errorIcon} />
                );
            }
        }

        return (
            <div className="status-wrapper">
                <div {...statusProps}>{status}</div>
                {typeof renderInput === "function" ? renderInput(this.input, this.handleChange) : (
                    <input
                        {...other}
                        ref={this.input}
                        onChange={this.handleInputChange}
                        type="text"
                    />
                )}
            </div>
        );
    }
}
